import React, { useEffect } from 'react';
import styled from 'styled-components';
import FlexboxContainer from 'components/UI/FlexboxContainer';
import { CANCELED, CANCELING, MANUFACTURING, PROCESSING } from 'constants/consignmentStates';
import { STORAGE_KEYS } from 'constants/general';
import { FontSize } from 'styles/themeSizes';
import { FontWeight } from 'lib/styles';
import { ConsignmentDetails } from 'data/Consignment';
import { CONSIGNMENT_TYPES } from 'constants/order';
import OrderPreviewItem from './OrderPreviewItem';
import ConsignmentEntry from 'data/ConsignmentEntry';
import OrderPreviewItemPrintable from './OrderPreviewItemPrintable';
import { useAPI } from 'lib/API';
import { emulateSplitConsignment } from 'requests/consignment';
import LoadingIndicator from 'components/UI/LoadingIndicator';
import { renderToast, toast } from 'components/UI/ToastNotification';

import WarningBanner from 'components/UI/WarningBanner';
import Link from 'view/Link';

const SubHeading = styled.div`
  font-size: ${FontSize.subLarge};
  font-weight: ${FontWeight.bold};
  margin-bottom: 10px;
`;

type SplittedItems = ConsignmentEntry[][];

/**
 * Function to split items by rental dates
 * @param items - Array of consignment entries
 * @returns Array of arrays of consignment entries
 */
function splitByRentalDates(items: ConsignmentEntry[]) {
    // Function to convert dates to a comparable format
    const formatDate = (date: any) => new Date(date).getTime();

    // Object to group items by rental dates
    const groupedItems: { [key: string]: ConsignmentEntry[] } = {};

    items.forEach((item) => {
        const startDate = formatDate(item.rentalStartDate);
        const endDate = formatDate(item.rentalEndDate);
        const key = `${startDate}-${endDate}`;

        if (!groupedItems[key]) {
            groupedItems[key] = [];
        }
        groupedItems[key].push(item);
    });

    // Convert the grouped items object to an array of arrays
    return Object.values(groupedItems);
}


interface OrderProcessPreviewProps {
    consignmentId?: string;
    setIsReviewPageMounted?: (isMounted: boolean) => void;
    setIsReviewPage?: (isReviewPage: boolean) => void;
    onConfirm: () => void;
    countryId: number;
    totalDiscounts?: number;
}

const OrderProcessPreview: React.FC<OrderProcessPreviewProps> = ({ consignmentId, setIsReviewPageMounted, setIsReviewPage, onConfirm, countryId, totalDiscounts }) => {

    const [splittedConsignment, setSplittedConsignment] = React.useState<any[]>([]);
    const orderPreviewData = JSON.parse(sessionStorage.getItem(STORAGE_KEYS.PREVIEW_ROUTE_DATA) || '');
    const consignmentDetailsResponse: ConsignmentDetails = orderPreviewData?.consignmentDetailsResponse || {};
    const fulfillmentRequest: ConsignmentEntry[] = orderPreviewData?.fulfillmentRequest || [];

    const splitConsignmentReq = useAPI({
        deferFn: emulateSplitConsignment,
        onResolve: (result) => {
            const splitedConsignments = result.map((splitConsignment: any) => {
                const newEntries = splitConsignment.entries.map((entry: any) => {
                    let foundEntry = consignmentDetailsResponse.consignmentEntries.find(consEntry => consEntry.entryNumber === entry.entryNumber);
                    if (!foundEntry) {
                        foundEntry = consignmentDetailsResponse.consignmentEntriesAddons.find(consEntry => consEntry.entryNumber === entry.entryNumber);
                    }
                    const selectableAttributes = foundEntry && foundEntry.selectableAttributes;
                    const attributeValues = foundEntry && foundEntry.attributeValues;
                    const productImages = foundEntry && foundEntry.productImages;
                    const priceBook = foundEntry?.priceBook;
                    const pricebookId = foundEntry?.pricebookId;
                    const pricebookName = foundEntry?.pricebookName;
                    const productVariantPrices = foundEntry?.productVariantPrices;
                    const parentEntryNumber = foundEntry?.parentEntryNumber;
                    const serialNumber = foundEntry?.serialNumber;
                    const entryNumber = foundEntry?.entryNumber;
                    const plannedUsage = foundEntry?.plannedUsage;
                    const unitOfMeasurement = foundEntry?.unitOfMeasurement;
                    const rentalStartDate = foundEntry?.rentalStartDate ? new Date(foundEntry?.rentalStartDate).getTime() : 0;
                    const rentalEndDate = foundEntry?.rentalEndDate ? new Date(foundEntry?.rentalEndDate).getTime() : 0;
                    const sku = entry?.productSKU;
                    const materialApiValidation = foundEntry?.materialApiValidation;
                    const badge = foundEntry?.badge;

                    return {
                        ...entry, selectableAttributes,
                        attributeValues, productImages, priceBook, pricebookId, pricebookName, productVariantPrices,
                        parentEntryNumber, serialNumber, entryNumber, plannedUsage, unitOfMeasurement, sku, rentalStartDate, rentalEndDate,
                        materialApiValidation, badge
                    }
                })

                const entryNumbers = splitConsignment.entries?.map((entry: any) => entry.entryNumber);
                return { ...splitConsignment, entries: newEntries, entryNumbers }
            });

            setSplittedConsignment(splitedConsignments);
            setIsReviewPageMounted && setIsReviewPageMounted(true);

        },
        onReject: err => {
            renderToast(toast.TYPE.ERROR, err.message);
            setIsReviewPageMounted && setIsReviewPageMounted(false);
            setIsReviewPage && setIsReviewPage(false);
        }
    });

    useEffect(() => {

        let entries: any = [];
        fulfillmentRequest.length > 0 && fulfillmentRequest.forEach((entry, i) => {
            let committedDate;
            let manufactureDate;

            if (entry.manufactureQuantity > 0) {
                manufactureDate = entry?.manufactureDate
                    ? new Date(entry.manufactureDate).setUTCHours(0, 0, 0, 0)
                    : new Date(entry.preliminaryDeliveryDate).setUTCHours(0, 0, 0, 0);
                committedDate = 0;
            } else if (entry.committedQuantity > 0) {
                committedDate = entry?.committedDate
                    ? new Date(entry.committedDate).setUTCHours(0, 0, 0, 0)
                    : new Date(entry.preliminaryDeliveryDate).setUTCHours(0, 0, 0, 0);
                manufactureDate = 0;
            } else {
                // Cancel-only or deleted entry — no committed/manufacture quantities
                // Set both dates to 0 to avoid overwriting user-changed dates during consolidation
                committedDate = 0;
                manufactureDate = 0;
            }

            let dd: any = {
                entryNumber: entry.entryNumber,
                quantity: entry.quantity || 0,
                productId: entry?.productId,
                committedQuantity: entry?.committedQuantity || 0,
                manufactureQuantity: entry?.manufactureQuantity || 0,
                cancelQuantity: entry.cancelQuantity || 0,
                committedDate,
                manufactureDate,
            };

            if (consignmentDetailsResponse.consignmentType === CONSIGNMENT_TYPES.RENT) {
                dd = {
                    ...dd,
                    rentalStartDate: entry?.rentalStartDate
                        ? new Date(entry.rentalStartDate).setUTCHours(0, 0, 0, 0)
                        : 0,
                    rentalEndDate: entry?.rentalEndDate
                        ? new Date(entry.rentalEndDate).setUTCHours(0, 0, 0, 0)
                        : 0,
                    committedDate:
                        entry?.committedQuantity > 0
                            ? new Date(entry.rentalStartDate).setUTCHours(0, 0, 0, 0)
                            : 0,
                    committedEndDate:
                        entry?.committedQuantity > 0
                            ? new Date(entry.rentalEndDate).setUTCHours(0, 0, 0, 0)
                            : 0,
                    manufactureDate:
                        entry?.manufactureQuantity > 0
                            ? new Date(entry.rentalStartDate).setUTCHours(0, 0, 0, 0)
                            : 0,
                    manufactureEndDate:
                        entry?.manufactureQuantity > 0
                            ? new Date(entry.rentalEndDate).setUTCHours(0, 0, 0, 0)
                            : 0,
                };
            }

            const existingIndex = entries.findIndex((e: any) => e.entryNumber === entry.entryNumber);
            if (existingIndex !== -1) {
                const existing = entries[existingIndex];

                entries[existingIndex] = {
                    ...existing,
                    ...dd,
                    committedQuantity: (existing.committedQuantity || 0) + (dd.committedQuantity || 0),
                    manufactureQuantity: (existing.manufactureQuantity || 0) + (dd.manufactureQuantity || 0),
                    cancelQuantity: (existing.cancelQuantity || 0) + (dd.cancelQuantity || 0),
                    quantity: (existing.quantity || 0) + (dd.quantity || 0),
                    // For dates, prefer the first non-zero value (or latest if that's needed)
                    committedDate: existing.committedDate || dd.committedDate,
                    manufactureDate: existing.manufactureDate || dd.manufactureDate,
                    rentalStartDate: existing.rentalStartDate || dd.rentalStartDate,
                    rentalEndDate: existing.rentalEndDate || dd.rentalEndDate,
                    committedEndDate: existing.committedEndDate || dd.committedEndDate,
                    manufactureEndDate: existing.manufactureEndDate || dd.manufactureEndDate,
                };
            } else {
                entries.push(dd);
            }
        });

        const modifiedPayload = entries.map((item: any) => {
            return {
                entryNumber: item.entryNumber,
                quantity: (item.committedQuantity || 0) + (item.manufactureQuantity || 0) + (item.cancelQuantity || 0),
                committedQuantity: item.committedQuantity,
                manufactureQuantity: item.manufactureQuantity,
                cancelQuantity: item.cancelQuantity,
                committedDate: item.committedDate || item.rentalStartDate || 0,
                manufactureDate: item.manufactureDate,
                rentalStartDate: item.rentalStartDate || 0,
                rentalEndDate: item.rentalEndDate || 0,
                committedEndDate: item.committedEndDate || item.rentalEndDate || 0,
                manufactureEndDate: item.manufactureEndDate || 0
            }
        });


        const splitConsignmentBody = {
            availableDate: consignmentDetailsResponse.availableDate ? new Date(consignmentDetailsResponse.availableDate).getTime() : 0,
            consignmentId: consignmentDetailsResponse.consignmentId,
            state: consignmentDetailsResponse.state,
            entries: modifiedPayload
        }

        sessionStorage.setItem(STORAGE_KEYS.SPLIT_CONSIGNMENT_BODY, JSON.stringify(splitConsignmentBody));

        splitConsignmentReq.run(consignmentId, splitConsignmentBody);
    }, [consignmentId]);


    if (splitConsignmentReq.isLoading) {
        return <LoadingIndicator />;
    }


    // Sort splitted consignments by state in descending order
    splittedConsignment.sort((a, b) => {
        const stateOrder = [CANCELED, CANCELING, MANUFACTURING, PROCESSING];
        return stateOrder.indexOf(b.state) - stateOrder.indexOf(a.state);
    });


    return (
        <>
            <FlexboxContainer justifyContent='space-between'>
                <FlexboxContainer padding='0' justifyContent="space-between" alignItems="center">
                    <SubHeading>{'Order update preview'}</SubHeading>
                </FlexboxContainer>
            </FlexboxContainer>

            <WarningBanner message={<>Click <Link className='text-underlined' onClick={onConfirm}>'Confirm'</Link> to proceed with the order only after all changes have been confirmed with customer.</>} />

            {
                splittedConsignment.length > 0 && splittedConsignment.map((item: any, index: number) => (
                    <OrderPreviewItem
                        key={index} // Provide a unique key for each mapped item
                        deliveryDate={item.availableDate || consignmentDetailsResponse.availableDate}
                        consignmentEntries={item.entries}
                        state={item.state}
                        isRental={true}
                        consignmentId={item.consignmentId}
                        deliveryTax={item.deliveryTax}
                        deliveryCost={item.deliveryCost}
                        orderTotalCost={item.totalPriceWithTax}
                        fulfillmentRequest={fulfillmentRequest}
                        countryId={countryId}
                        consignmentSubtotal={item.entries.reduce((acc: number, entry: any) => acc + (entry.totalPrice + entry.surcharge), 0)}
                        consignmentTotalTax={item.entries.reduce((acc: number, entry: any) => acc + (entry.taxAmount), 0)}
                        consignmentTotalSurcharges={item.entries.reduce((acc: number, entry: any) => acc + (entry.surcharge), 0)}
                        consignmentTotalDiscounts={totalDiscounts}
                    />
                ))
            }

            <div id="order-update-preview-printable-content" style={{ display: 'none' }}>

                {
                    splittedConsignment.length > 0 && splittedConsignment.map((item: any, index: number) => (
                        <OrderPreviewItemPrintable
                            key={index} // Provide a unique key for each mapped item
                            deliveryDate={item.availableDate || consignmentDetailsResponse.availableDate}
                            consignmentEntries={item.entries}
                            state={item.state}
                            isRental={true}
                            consignmentId={item.consignmentId}
                            deliveryTax={item.deliveryTax}
                            deliveryCost={item.deliveryCost}
                            orderTotalCost={item.totalPriceWithTax}
                            fulfillmentRequest={fulfillmentRequest}
                            totalDiscounts={totalDiscounts}
                        />
                    ))
                }

            </div>
        </>);
}

export default OrderProcessPreview;